import { EstablishmentCheckout, isRestrictionAll, ShippingOption } from './types';

export const SHIPPING_OPTION_KEYS = [
  'shippingOptionInPlace',
  'shippingOptionTakeAway',
  'shippingOptionDelivery',
  'shippingOptionParcel',
] as const;

export type ShippingOptionKey = (typeof SHIPPING_OPTION_KEYS)[number];

/**
 * Un método de pago recién creado o activado nunca debe nacer invisible: las formas de entrega
 * con `allowedPaymentMethods` explícita (guardada antes de que el método existiera) lo excluyen
 * sin que el comercio lo haya decidido. Devuelve, por forma de entrega, SOLO los valores que
 * faltan — los callers los agregan con `arrayUnion` para no reescribir la lista completa (una
 * lectura vieja no debe poder borrar lo que otro guardado ya agregó). Las listas `['ALL']` no
 * necesitan cambios y las exclusiones posteriores del comercio no pasan por acá.
 */
export function getMissingPaymentMethodsByShippingOption(
  checkout: Partial<Pick<EstablishmentCheckout, ShippingOptionKey>> | null | undefined,
  methodValues: string[],
): Partial<Record<ShippingOptionKey, string[]>> {
  const missingByShippingOption: Partial<Record<ShippingOptionKey, string[]>> = {};

  if (!checkout || methodValues.length === 0) {
    return missingByShippingOption;
  }

  for (const key of SHIPPING_OPTION_KEYS) {
    const allowList: string[] | undefined = checkout[key]?.allowedPaymentMethods;

    if (!Array.isArray(allowList) || isRestrictionAll(allowList)) {
      continue;
    }

    const missing = methodValues.filter(value => !allowList.includes(value));

    if (missing.length > 0) {
      missingByShippingOption[key] = missing;
    }
  }

  return missingByShippingOption;
}

export function isPaymentMethodAllowedForShippingOption(
  shippingOption: Pick<ShippingOption, 'allowedPaymentMethods'> | null | undefined,
  paymentMethodValue: string,
): boolean {
  const allowList: readonly string[] | undefined = shippingOption?.allowedPaymentMethods;

  return isRestrictionAll(allowList) || allowList.includes(paymentMethodValue);
}
